import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// Sprint 6.9.8 — Solde réel = dernière clôture validée + opérations APRÈS cette clôture.
// Si aucune clôture validée pour un compte → fallback sur solde_initial + toutes ops validees/comptabilisees.

export interface SoldeReelDetail {
  compte_id: string;
  compte_type: 'caisse' | 'banque';
  intitule: string;
  /** Solde au moment de la dernière clôture (= solde_reel comptage si clôture validée existe). */
  solde_base: number;
  /** Date de la dernière clôture validée (null si aucune). */
  derniere_cloture: string | null;
  /** Sum des opérations APRÈS la dernière clôture (validees+comptabilisees). */
  delta_apres_cloture: number;
  /** Solde final = solde_base + delta_apres_cloture. */
  solde_reel: number;
  /** True si la base provient d'une clôture (sinon solde_initial). */
  has_cloture: boolean;
}

export interface SoldeReelResult {
  soldeReelTotal: number;
  soldeHistoriqueTotal: number;
  derniereClotureGlobale: string | null;
  hasAnyCloture: boolean;
  details: SoldeReelDetail[];
}

interface CompteRow {
  id: string;
  intitule: string;
  solde_initial: number;
  is_active: boolean;
  type: 'caisse' | 'banque';
  entreprise_id: string;
}

interface OpAggRow {
  caisse_id: string | null;
  compte_bancaire_id: string | null;
  sens: 'entree' | 'sortie' | 'transfert';
  montant_dzd: number;
  date_operation: string;
}

interface ClotureRow {
  caisse_id: string | null;
  compte_bancaire_id: string | null;
  date_cloture: string;
  solde_reel: number;
  statut: string;
}

export function useSoldeReel(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['solde_reel', entrepriseId ?? '__all__'],
    queryFn: async (): Promise<SoldeReelResult> => {
      // 1. Caisses + comptes actifs DZD
      const caissesQ = supabase
        .from('caisses')
        .select('id, nom, solde_initial, is_active, devise_code, entreprise_id')
        .eq('is_active', true)
        .eq('devise_code', 'DZD');
      const comptesQ = supabase
        .from('comptes_bancaires')
        .select('id, intitule, solde_initial, is_active, devise_code, entreprise_id')
        .eq('is_active', true)
        .eq('devise_code', 'DZD');
      if (entrepriseId) {
        caissesQ.eq('entreprise_id', entrepriseId);
        comptesQ.eq('entreprise_id', entrepriseId);
      }
      const [caissesRes, comptesRes] = await Promise.all([caissesQ, comptesQ]);
      if (caissesRes.error) throw caissesRes.error;
      if (comptesRes.error) throw comptesRes.error;

      const comptes: CompteRow[] = [
        ...(
          (caissesRes.data ?? []) as {
            id: string;
            nom: string;
            solde_initial: number;
            is_active: boolean;
            entreprise_id: string;
          }[]
        ).map((c) => ({
          id: c.id,
          intitule: c.nom,
          solde_initial: Number(c.solde_initial),
          is_active: c.is_active,
          type: 'caisse' as const,
          entreprise_id: c.entreprise_id,
        })),
        ...(
          (comptesRes.data ?? []) as {
            id: string;
            intitule: string;
            solde_initial: number;
            is_active: boolean;
            entreprise_id: string;
          }[]
        ).map((c) => ({
          id: c.id,
          intitule: c.intitule,
          solde_initial: Number(c.solde_initial),
          is_active: c.is_active,
          type: 'banque' as const,
          entreprise_id: c.entreprise_id,
        })),
      ];

      // 2. Dernière clôture validée par caisse_id ou compte_bancaire_id
      const cloturesQ = supabase
        .from('clotures')
        .select('caisse_id, compte_bancaire_id, date_cloture, solde_reel, statut')
        .eq('statut', 'validee')
        .order('date_cloture', { ascending: false });
      if (entrepriseId) cloturesQ.eq('entreprise_id', entrepriseId);
      const cloturesRes = await cloturesQ;
      if (cloturesRes.error) throw cloturesRes.error;
      const clotures = (cloturesRes.data ?? []) as ClotureRow[];

      const lastClotureByCompte = new Map<string, { date: string; solde_reel: number }>();
      for (const c of clotures) {
        const key = c.caisse_id ?? c.compte_bancaire_id;
        if (!key) continue;
        if (!lastClotureByCompte.has(key)) {
          lastClotureByCompte.set(key, { date: c.date_cloture, solde_reel: Number(c.solde_reel) });
        }
      }

      // 3. Toutes les opérations validées/comptabilisées
      const opsQ = supabase
        .from('operations')
        .select('caisse_id, compte_bancaire_id, sens, montant_dzd, date_operation')
        .in('statut', ['validee', 'comptabilisee']);
      if (entrepriseId) opsQ.eq('entreprise_id', entrepriseId);
      const opsRes = await opsQ;
      if (opsRes.error) throw opsRes.error;
      const ops = (opsRes.data ?? []) as OpAggRow[];

      // 4. Calcul par compte
      const details: SoldeReelDetail[] = [];
      let soldeReelTotal = 0;
      let soldeHistoriqueTotal = 0;
      let derniereClotureGlobale: string | null = null;
      let hasAnyCloture = false;

      for (const c of comptes) {
        const cloture = lastClotureByCompte.get(c.id);
        const isCaisse = c.type === 'caisse';
        const matchOps = ops.filter((op) =>
          isCaisse ? op.caisse_id === c.id : op.compte_bancaire_id === c.id,
        );

        // Solde historique = solde_initial + toutes les ops
        const totalOps = matchOps.reduce((acc, op) => {
          const m = Number(op.montant_dzd);
          if (op.sens === 'entree') return acc + m;
          if (op.sens === 'sortie') return acc - m;
          return acc;
        }, 0);
        const soldeHistorique = c.solde_initial + totalOps;

        // Solde réel = base + delta après clôture
        let solde_base: number;
        let delta_apres_cloture: number;
        if (cloture) {
          solde_base = cloture.solde_reel;
          const opsApres = matchOps.filter((op) => op.date_operation > cloture.date);
          delta_apres_cloture = opsApres.reduce((acc, op) => {
            const m = Number(op.montant_dzd);
            if (op.sens === 'entree') return acc + m;
            if (op.sens === 'sortie') return acc - m;
            return acc;
          }, 0);
          hasAnyCloture = true;
          if (!derniereClotureGlobale || cloture.date > derniereClotureGlobale) {
            derniereClotureGlobale = cloture.date;
          }
        } else {
          solde_base = c.solde_initial;
          delta_apres_cloture = totalOps;
        }
        const solde_reel = solde_base + delta_apres_cloture;

        details.push({
          compte_id: c.id,
          compte_type: c.type,
          intitule: c.intitule,
          solde_base,
          derniere_cloture: cloture?.date ?? null,
          delta_apres_cloture,
          solde_reel,
          has_cloture: !!cloture,
        });
        soldeReelTotal += solde_reel;
        soldeHistoriqueTotal += soldeHistorique;
      }

      return {
        soldeReelTotal,
        soldeHistoriqueTotal,
        derniereClotureGlobale,
        hasAnyCloture,
        details,
      };
    },
  });
}
